import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Animated, Platform, Pressable, StyleSheet, View } from 'react-native';
import { Image } from 'expo-image';
import { usePathname } from 'expo-router';
import {
  getPetById,
  loadPetHoverImage,
  loadPetImage,
  loadPetStreamingImages,
} from '@/constants/pets';
import {
  DESKTOP_PET_BOTTOM,
  DESKTOP_PET_RIGHT,
  DESKTOP_PET_SIZE,
  isDesktopPetOccupyingComposer,
} from '@/constants/utils/desktopPetLayout';
import { accountApi } from '@/lib/api';
import { subscribePetReplyCompleted } from '@/lib/socket';
import { isArtifactSidebarType } from '@/constants/utils/files';
import {
  useAuthStore,
  useChatStore,
  useChatInputStore,
  useModalStore,
  useRouteStore,
  useUIStore,
  useVoiceModeStore,
} from '@/store';
import { useArtifactViewerStore } from '@/store/artifactViewer';
import useResponsiveness from '@/hooks/useResponsiveness';
import { useDesktopPetComposerLayout } from '@/hooks/useDesktopPetComposerClearance';
import Tooltip from '@/components/Tooltip';
import Text from '@/components/Typography/Text';
import { useTheme } from '@/context/ThemeContext';
import usePetMotion from './usePetMotion';

const SPEECH_DURATION = 12000;
const getSpeechReplyTarget = () => 2 + Math.floor(Math.random() * 2);

const styles = StyleSheet.create({
  host: {
    position: Platform.OS === 'web' ? 'fixed' : 'absolute',
    right: DESKTOP_PET_RIGHT,
    bottom: DESKTOP_PET_BOTTOM,
    width: DESKTOP_PET_SIZE,
    height: DESKTOP_PET_SIZE,
    zIndex: 900,
    pointerEvents: 'box-none',
  },
  button: {
    width: DESKTOP_PET_SIZE,
    height: DESKTOP_PET_SIZE,
    alignItems: 'center',
    justifyContent: 'flex-end',
    borderRadius: 24,
    ...Platform.select({
      web: {
        cursor: 'pointer',
        transition: 'opacity 120ms ease',
      },
      default: {},
    }),
  },
  pressed: {
    opacity: 0.82,
  },
  image: {
    width: 100,
    height: 100,
  },
  shadow: {
    position: 'absolute',
    bottom: 3,
    width: 56,
    height: 4,
    borderRadius: 30,
    backgroundColor: 'rgba(0, 0, 0, 0.14)',
  },
  speechBubble: {
    position: 'absolute',
    right: 74,
    bottom: 88,
    width: 176,
    paddingHorizontal: 13,
    paddingVertical: 9,
    borderWidth: 1,
    borderRadius: 14,
    ...Platform.select({
      web: {
        boxShadow: '0 5px 18px rgba(0, 0, 0, 0.14)',
      },
      default: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.14,
        shadowRadius: 9,
      },
    }),
  },
  speechText: {
    fontSize: 13,
    lineHeight: 18,
    textAlign: 'center',
  },
  speechTail: {
    position: 'absolute',
    right: 16,
    bottom: -6,
    width: 11,
    height: 11,
    borderRightWidth: 1,
    borderBottomWidth: 1,
    transform: [{ rotate: '45deg' }],
  },
});

export default function DesktopPetHost() {
  const { colors } = useTheme();
  const { isMD } = useResponsiveness();
  const { isVisible: hasComposerRoom } = useDesktopPetComposerLayout();
  const user = useAuthStore(state => state.user);
  const selectedPetId = user?.preferences?.appearance?.selectedPet || null;
  const isModalOpen = useModalStore(state => state.isOpen);
  const { openModal } = useModalStore(state => state.actions);
  const isVoiceModeActive = useVoiceModeStore(state => state.isActive);
  const isContextSidebarVisible = useUIStore(state => state.isContextSidebarVisible);
  const contextSidebarType = useUIStore(state => state.contextSidebarType);
  const isArtifactViewerOpen = useArtifactViewerStore(state => state.isOpen);
  const isArtifactOpen = isArtifactViewerOpen
    || (isContextSidebarVisible && isArtifactSidebarType(contextSidebarType));
  const pathname = usePathname();
  // Pets only live inside chat screens: conversations (/c), agent chats (/g),
  // and model chats (/m). Everywhere else (home, explore, settings, ...) they
  // should stay hidden.
  const routeStoreConversationId = useRouteStore(state => state.currentLibraryId);
  const pathnameConversationId = pathname?.match(/^\/c\/([^/]+)/)?.[1] || null;
  const currentConversationId = pathnameConversationId || routeStoreConversationId;
  const hasComposerDraft = useChatInputStore(state => {
    const draft = state.inputStates?.[state.currentContextId];
    return Boolean(draft?.text?.trim() || draft?.files?.length);
  });
  const isInferenceActive = useChatStore(state => {
    const activeChat = (state.chats || []).find(chat => chat.id === currentConversationId);
    return Boolean(
      activeChat?.isInferencing ||
      activeChat?.isInferencingWaiting ||
      (activeChat?.messages || []).some(message =>
        message?.role === 'assistant' &&
        (message.isStreaming || message.isTyping || message.isWaiting || message.isThinking)
      )
    );
  });
  const pet = useMemo(() => getPetById(selectedPetId), [selectedPetId]);
  const speechCadenceByConversation = useRef(new Map());
  const speechRequestVersion = useRef(0);
  const isSpeechRequestPending = useRef(false);
  const speechHideTimer = useRef(null);
  const [streamingFrameIndex, setStreamingFrameIndex] = useState(-1);
  const [isHovered, setIsHovered] = useState(false);
  const [petImages, setPetImages] = useState({
    default: null,
    hover: null,
    streaming: [],
  });
  const [speechBubble, setSpeechBubble] = useState(null);
  const isDesktop = Platform.OS === 'web' || Platform.OS === 'macos';
  const petImage = petImages.default;
  const hoverImage = petImages.hover;
  const streamingImages = petImages.streaming;
  const isVisible = Boolean(petImage) && hasComposerRoom && !isModalOpen
    && isDesktopPetOccupyingComposer({
      pathname,
      isDesktop,
      isMD,
      hasPet: Boolean(pet),
      isVoiceModeActive,
      isArtifactOpen,
    });
  const isInteractive = isVisible && !isInferenceActive && !hasComposerDraft;
  const isPetHovered = isInteractive && isHovered;
  const { animatedStyle, reduceMotion } = usePetMotion({
    isVisible,
    isHovered: isPetHovered,
    isWorking: isInferenceActive,
  });

  useEffect(() => {
    // Removing/disabling a native view does not always send mouse-exit.
    setIsHovered(false);
  }, [isInteractive, pathname, selectedPetId]);

  useEffect(() => {
    let cancelled = false;
    setPetImages({ default: null, hover: null, streaming: [] });

    if (!pet) return undefined;

    loadPetImage(pet)
      .then(image => {
        if (!cancelled) {
          setPetImages(current => ({ ...current, default: image }));
        }
      })
      .catch(error => console.error(`Unable to load ${pet.name}'s image:`, error));

    // Warm the greeting image alongside the idle image, before the first hover.
    loadPetHoverImage(pet)
      .then(image => {
        if (!cancelled) {
          setPetImages(current => ({ ...current, hover: image }));
        }
      })
      .catch(error => console.error(`Unable to load ${pet.name}'s hover image:`, error));

    return () => {
      cancelled = true;
    };
  }, [pet]);

  useEffect(() => {
    let cancelled = false;
    if (!pet || !isInferenceActive || streamingImages.length > 0) return undefined;

    loadPetStreamingImages(pet)
      .then(images => {
        if (!cancelled) {
          setPetImages(current => ({ ...current, streaming: images }));
        }
      })
      .catch(error => console.error(`Unable to load ${pet.name}'s animation images:`, error));

    return () => {
      cancelled = true;
    };
  }, [isInferenceActive, pet, streamingImages.length]);

  useEffect(() => {
    const frames = streamingImages;
    if (!isVisible || !isInferenceActive || frames.length === 0) {
      setStreamingFrameIndex(-1);
      return undefined;
    }

    const firstFrame = Math.floor(Math.random() * frames.length);
    setStreamingFrameIndex(firstFrame);

    if (reduceMotion || frames.length < 2) {
      return undefined;
    }

    const interval = setInterval(() => {
      setStreamingFrameIndex(current => (current + 1) % frames.length);
    }, 1100);
    return () => clearInterval(interval);
  }, [isInferenceActive, isVisible, reduceMotion, streamingImages]);

  useEffect(() => {
    speechRequestVersion.current += 1;
    isSpeechRequestPending.current = false;
    clearTimeout(speechHideTimer.current);
    setSpeechBubble(null);
  }, [currentConversationId, selectedPetId]);

  useEffect(() => {
    return subscribePetReplyCompleted(completedConversationId => {
      if (
        completedConversationId !== currentConversationId ||
        !isDesktop ||
        !hasComposerRoom ||
        isMD ||
        !pet ||
        isVoiceModeActive ||
        isModalOpen ||
        isArtifactOpen ||
        isSpeechRequestPending.current
      ) {
        return;
      }

      const cadence = speechCadenceByConversation.current.get(completedConversationId) || {
        completedReplies: 0,
        // Let the user discover the feature on the first completed reply.
        // Subsequent reactions stay occasional so the pet is not distracting.
        target: 1,
      };
      cadence.completedReplies += 1;
      speechCadenceByConversation.current.set(completedConversationId, cadence);
      if (cadence.completedReplies < cadence.target) return;

      cadence.completedReplies = 0;
      cadence.target = getSpeechReplyTarget();
      const requestVersion = ++speechRequestVersion.current;
      isSpeechRequestPending.current = true;

      accountApi.generatePetSpeech(completedConversationId)
        .then(speech => {
          if (requestVersion !== speechRequestVersion.current || !speech) return;
          setSpeechBubble(speech);
          clearTimeout(speechHideTimer.current);
          speechHideTimer.current = setTimeout(() => {
            if (requestVersion === speechRequestVersion.current) {
              setSpeechBubble(null);
            }
          }, SPEECH_DURATION);
        })
        .catch(error => {
          console.error('Unable to generate chat companion speech:', error);
        })
        .finally(() => {
          if (requestVersion === speechRequestVersion.current) {
            isSpeechRequestPending.current = false;
          }
        });
    });
  }, [
    currentConversationId,
    isDesktop,
    hasComposerRoom,
    isArtifactOpen,
    isModalOpen,
    isVoiceModeActive,
    isMD,
    pet,
  ]);

  useEffect(() => () => {
    speechRequestVersion.current += 1;
    clearTimeout(speechHideTimer.current);
  }, []);

  // The companion picker is opt-in only (pet click, Settings → Appearance,
  // home). It used to auto-open right after new-user onboarding, which landed
  // as an unexpected second popup; that first-run trigger was removed.

  const handlePress = useCallback(() => {
    openModal('PetPickerModal');
  }, [openModal]);

  if (!isVisible) return null;

  const streamingImage = isInferenceActive && streamingFrameIndex >= 0
    ? streamingImages[streamingFrameIndex]
    : null;
  const displayedImage = isPetHovered && hoverImage
    ? hoverImage
    : streamingImage || petImage;

  const activityLabel = isInferenceActive
    ? `${pet.name} is helping with your answer`
    : `${pet.name}, your chat companion. Press to change companion.`;

  return (
    <View
      // The floating companion can overlap Send/Stop. Let those controls
      // receive clicks while composing or working, including on macOS where
      // the transparent portion of the companion is still a native hit target.
      pointerEvents={isInteractive ? 'box-none' : 'none'}
      accessibilityElementsHidden={!isInteractive}
      importantForAccessibility={isInteractive ? 'auto' : 'no-hide-descendants'}
      style={styles.host}
    >
      {speechBubble ? (
        <View
          accessibilityElementsHidden
          importantForAccessibility="no-hide-descendants"
          pointerEvents="none"
          style={[
            styles.speechBubble,
            {
              backgroundColor: colors.solid_color_block,
              borderColor: colors.border_color_secondary,
            },
          ]}
        >
          <Text color="primary" style={styles.speechText}>{speechBubble}</Text>
          <View
            style={[
              styles.speechTail,
              {
                backgroundColor: colors.solid_color_block,
                borderColor: colors.border_color_secondary,
              },
            ]}
          />
        </View>
      ) : null}
      <Tooltip disabled={!isInteractive} label={isInferenceActive ? `${pet.name} is working…` : `${pet.name} · Change companion`}>
        <Pressable
          disabled={!isInteractive}
          accessibilityRole="button"
          accessibilityLabel={activityLabel}
          accessibilityState={{ busy: isInferenceActive }}
          onHoverIn={() => setIsHovered(isInteractive)}
          onHoverOut={() => setIsHovered(false)}
          onPress={handlePress}
          style={({ pressed }) => [styles.button, pressed && styles.pressed]}
        >
          <View pointerEvents="none" style={styles.shadow} />
          <Animated.View pointerEvents="none" style={[styles.image, animatedStyle]}>
            <Image
              source={displayedImage}
              style={styles.image}
              contentFit="contain"
              transition={reduceMotion ? 0 : 100}
            />
          </Animated.View>
        </Pressable>
      </Tooltip>
    </View>
  );
}
